import { X, Smile } from 'lucide-react'
import { useState, useCallback, useMemo } from 'react'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { ScrollArea } from '@/common/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { Skeleton } from '@/common/ui/skeleton'

import { useQueryStickerPackages } from '../../api/hooks'

/**
 * Generate LINE sticker preview URL from CDN
 * @param stickerId - The sticker ID
 * @returns CDN URL for sticker preview image
 */
function getStickerPreviewUrl(stickerId: string): string {
	return `https://stickershop.line-scdn.net/stickershop/v1/sticker/${stickerId}/android/sticker.png`
}

export interface StickerSelection {
	packageId: string
	stickerId: string
}

interface StickerPickerProps {
	onSelect: (sticker: StickerSelection) => void
	onClose: () => void
}

export function StickerPicker({ onSelect, onClose }: StickerPickerProps) {
	const { data: packagesResponse, isLoading } = useQueryStickerPackages()
	const packages = useMemo(
		() => packagesResponse?.data ?? [],
		[packagesResponse],
	)

	const [selectedPackage, setSelectedPackage] = useState<string | null>(null)
	const [loadedStickers, setLoadedStickers] = useState<Set<string>>(new Set())
	const [errorStickers, setErrorStickers] = useState<Set<string>>(new Set())

	// Set initial selected package when data loads
	const activePackageId = useMemo(() => {
		if (selectedPackage) return selectedPackage
		if (packages.length > 0) return packages[0].packageId
		return null
	}, [selectedPackage, packages])

	const handleStickerLoad = useCallback((stickerId: string) => {
		setLoadedStickers((prev) => new Set(prev).add(stickerId))
	}, [])

	const handleStickerError = useCallback((stickerId: string) => {
		setErrorStickers((prev) => new Set(prev).add(stickerId))
	}, [])

	const handleSelect = useCallback(
		(packageId: string, stickerId: string) => {
			onSelect({ packageId, stickerId })
		},
		[onSelect],
	)

	if (isLoading) {
		return (
			<div className="border-t bg-muted/30">
				<div className="flex items-center justify-between border-b px-4 py-2">
					<div className="flex items-center gap-2">
						<Smile className="h-4 w-4 text-primary" />
						<span className="text-sm font-medium">สติกเกอร์</span>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={onClose}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
				<div className="p-3">
					<div className="mb-3 flex gap-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<Skeleton key={i} className="h-8 w-20" />
						))}
					</div>
					<div className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-8">
						{Array.from({ length: 16 }).map((_, i) => (
							<Skeleton key={i} className="aspect-square rounded-lg" />
						))}
					</div>
				</div>
			</div>
		)
	}

	if (packages.length === 0) {
		return (
			<div className="border-t bg-muted/30">
				<div className="flex items-center justify-between border-b px-4 py-2">
					<div className="flex items-center gap-2">
						<Smile className="h-4 w-4 text-primary" />
						<span className="text-sm font-medium">สติกเกอร์</span>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={onClose}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
				<div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
					ไม่พบสติกเกอร์
				</div>
			</div>
		)
	}

	return (
		<div className="border-t bg-muted/30">
			<div className="flex items-center justify-between border-b px-4 py-2">
				<div className="flex items-center gap-2">
					<Smile className="h-4 w-4 text-primary" />
					<span className="text-sm font-medium">สติกเกอร์</span>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6"
					onClick={onClose}
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			<Tabs
				value={activePackageId ?? undefined}
				onValueChange={setSelectedPackage}
				className="w-full"
			>
				<div className="overflow-x-auto border-b">
					<TabsList className="inline-flex w-max min-w-full justify-start gap-1 rounded-none bg-transparent px-2">
						{packages.map((pkg) => (
							<TabsTrigger
								key={pkg.packageId}
								value={pkg.packageId}
								className="shrink-0 rounded-none border-b-2 border-transparent px-3 py-2 text-xs data-[state=active]:border-primary data-[state=active]:bg-transparent"
							>
								{pkg.name}
							</TabsTrigger>
						))}
					</TabsList>
				</div>

				{packages.map((pkg) => (
					<TabsContent
						key={pkg.packageId}
						value={pkg.packageId}
						className="mt-0"
					>
						<ScrollArea className="h-48">
							<div className="grid grid-cols-5 gap-2 p-3 sm:grid-cols-6 md:grid-cols-8">
								{pkg.stickers?.map((sticker) => {
									const stickerKey = `${pkg.packageId}-${sticker.stickerId}`
									const isLoaded = loadedStickers.has(stickerKey)
									const hasError = errorStickers.has(stickerKey)

									return (
										<button
											key={sticker.stickerId}
											onClick={() =>
												handleSelect(pkg.packageId, sticker.stickerId)
											}
											className={cn(
												'group relative flex aspect-square items-center justify-center rounded-lg border bg-background p-1 transition-all hover:border-primary hover:bg-primary/5',
												hasError && 'opacity-50',
											)}
											title={`Sticker ${sticker.stickerId}`}
											disabled={hasError}
										>
											{!isLoaded && !hasError && (
												<div className="absolute inset-0 flex items-center justify-center">
													<div className="h-4 w-4 animate-pulse rounded-full bg-muted" />
												</div>
											)}
											<img
												src={
													sticker.stickerUrl ||
													getStickerPreviewUrl(sticker.stickerId)
												}
												alt={`Sticker ${sticker.stickerId}`}
												className={cn(
													'h-full w-full object-contain transition-transform group-hover:scale-110',
													!isLoaded && 'opacity-0',
												)}
												loading="lazy"
												onLoad={() => handleStickerLoad(stickerKey)}
												onError={() => handleStickerError(stickerKey)}
											/>
										</button>
									)
								})}
							</div>
						</ScrollArea>
					</TabsContent>
				))}
			</Tabs>
		</div>
	)
}

// Export sticker preview URL helper for use in other components
export { getStickerPreviewUrl }
